import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { GeneriqueData, WorkflowEtape } from '../models/models';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonColor, ButtonType, GeneriqueDataType } from '../constants/enums';
import MyTable from '../components/MyTable';
import { useDialog } from '../components/DialogComponent';
import { useSnackbar } from '../components/SnackbarProvider';
import WorkflowEtapeForm from '../components/Forms/WorkflowEtapeForm';
import GeneriqueDataForm from '../components/Forms/GeneriqueDataForm';
import WorkflowVisualizer from '../components/WorkflowVisualizer';
import {
  fetchWorkflowEtape,
  setSelectedWorkflowEtape,
  clearWorkflowEtapeData,
} from '../reduxStore/WorkflowEtapeSlice';
import { fetchGeneriqueDataByType } from '../reduxStore/GeneriqueDataSlice';
import { fetchSysGeneriqueDataTypes } from '../reduxStore/SysGeneriqueDataTypeSlice';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppOutlineButton from '../components/layout/AppOutlineButton';

const GestionWorkflow: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const { workflowEtapeData, total, successMessage, error } =
    useSelector((state: any) => state.WorkflowEtapeSlice);

  const { generiqueData: workflowList } = useSelector((state: RootState) => state.GeneriqueDataSlice);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null);
  const [fetchParams, setFetchParams] = useState({ limit: 50, offset: 0 });

  const [EtapeDialog, openEtapeDialog, closeEtapeDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });
  const [WorkflowDialog, openWorkflowDialog, closeWorkflowDialog] = useDialog({
    dialogProps: { maxWidth: 'xs', fullWidth: true },
  });

  useEffect(() => {
    dispatch(fetchSysGeneriqueDataTypes());
    dispatch(fetchSysTypeDocuments());
    dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.WORKFLOW }));
    return () => {
      dispatch(clearWorkflowEtapeData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedWorkflowId !== null) {
      dispatch(fetchWorkflowEtape({
        workflow_id: selectedWorkflowId,
        source_code: null,
        ...fetchParams,
      }));
    }
  }, [dispatch, selectedWorkflowId, fetchParams, successMessage]);

  useEffect(() => {
    if (successMessage) showSnackbar(successMessage, 'success');
    if (error) showSnackbar(error, 'error');
  }, [successMessage, error, dispatch, showSnackbar]);

  const handleSelectWorkflow = (id: number) => {
    setSelectedWorkflowId(id);
    setFetchParams({ limit: 50, offset: 0 });
  };

  const handlePageChange = (limit: number, offset: number) => {
    setFetchParams({ limit, offset });
  };

  const headers: TableHeader<WorkflowEtape>[] = [
    { key: 'workflow_nom', label: 'Workflow' },
    { key: 'doc_source_code', label: 'Source', render: (row) => `${row.source_libelle ?? ''} (${row.doc_source_code})` },
    { key: 'doc_cible_code',  label: 'Cible',  render: (row) => `${row.cible_libelle  ?? ''} (${row.doc_cible_code})` },
    { key: 'ordre', label: 'Ordre' },
  ];

  const tableActions: TableAction<WorkflowEtape>[] = [
    {
      label: 'Modifier',
      color: ButtonColor.PRIMARY,
      type: ButtonType.EDIT,
      callback: (row: WorkflowEtape) => {
        dispatch(setSelectedWorkflowEtape(row));
        openEtapeDialog();
      },
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Workflows de documents"
        icon={<AccountTreeIcon />}
        addLabel="Ajouter une transition"
        onAdd={() => { dispatch(setSelectedWorkflowEtape(null)); openEtapeDialog(); }}
        actions={
          <AppOutlineButton
            startIcon={<AddIcon />}
            onClick={openWorkflowDialog}
          >
            Nouveau workflow
          </AppOutlineButton>
        }
      />

      <AppSurface sx={{ mb: 2 }} padding={2}>
        <TextField
          select
          label="Workflow actif"
          value={selectedWorkflowId ?? ''}
          onChange={(e) => handleSelectWorkflow(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 260 }}
        >
          {workflowList.map((w: GeneriqueData) => (
            <MenuItem key={w.id} value={w.id}>{w.nom}</MenuItem>
          ))}
        </TextField>

        {selectedWorkflowId === null && (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Sélectionnez un workflow pour voir ses transitions.
          </Typography>
        )}

        {selectedWorkflowId !== null && (
          <Box mt={2}>
            <WorkflowVisualizer etapes={workflowEtapeData} />
          </Box>
        )}
      </AppSurface>

      {selectedWorkflowId !== null && (
        <AppSurface>
          <MyTable
            headers={headers}
            data={workflowEtapeData}
            actions={tableActions}
            onPageChange={handlePageChange}
            totalRows={total}
          />
        </AppSurface>
      )}

      <EtapeDialog>
        <WorkflowEtapeForm
          workflows={workflowList}
          onSuccess={() => {
            closeEtapeDialog();
            if (selectedWorkflowId) {
              dispatch(fetchWorkflowEtape({ workflow_id: selectedWorkflowId, source_code: null, ...fetchParams }));
            }
          }}
        />
      </EtapeDialog>

      <WorkflowDialog>
        <Typography variant="h6" sx={{ mb: 2 }}>Créer un workflow</Typography>
        <GeneriqueDataForm
          typeDonnee={GeneriqueDataType.WORKFLOW}
          onCancel={closeWorkflowDialog}
          onSuccess={() => {
            closeWorkflowDialog();
            dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.WORKFLOW }));
          }}
        />
      </WorkflowDialog>
    </AppPage>
  );
};

export default GestionWorkflow;
